import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import axiosInstance from '../../../../api/axiosInstance';
import { getUserInfoDetails } from '../../../../utlis';
import { getAllClasses } from '../../../../api/training';
import CandidateTrainingsHeader from './components/CandidateTrainingsHeader';
import CandidateTrainingsTable from './components/CandidateTrainingsTable';
import CandidateTrainingsModal from './components/CandidateTrainingsModal';
import {
  initialCandidateTraining,
  filterCategories,
  prepareTrainingForEdit,
  prepareTrainingPayload,
} from './candidateTrainingUtils';

const { companyId, clientId } = getUserInfoDetails();

const CandidateTrainings = () => {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: initialCandidateTraining,
  });

  // Get candidateId from Redux store
  const candidateId = useSelector((state) => state.personal.candidateId);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingRecordId, setEditingRecordId] = useState(null);
  const [currentTraining, setCurrentTraining] = useState(initialCandidateTraining);

  // Loading states
  const [isLoading, setLoading] = useState(false);
  const [isSubmitting, setSubmitting] = useState(false);

  // Data state
  const [data, setData] = useState([]);
  const [classes, setClasses] = useState([]);
  const [classesLoading, setClassesLoading] = useState(false);
  const [pageSize, setPageSize] = useState(15);
  const [searchTerm, setSearchTerm] = useState('');

  // Filter state
  const [isFilterPopupOpen, setFilterPopupOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState([]);

  // Fetch classes for dropdown
  useEffect(() => {
    const fetchClasses = async () => {
      setClassesLoading(true);
      try {
        const response = await getAllClasses(1, 100);
        const records = response?.data?.data?.records || response?.data?.records || [];
        setClasses(Array.isArray(records) ? records : []);
      } catch (err) {
        console.error('Error fetching classes:', err);
      } finally {
        setClassesLoading(false);
      }
    };
    fetchClasses();
  }, []);

  // Modal handlers
  const openModal = () => {
    setIsEditMode(false);
    setEditingRecordId(null);
    setCurrentTraining(initialCandidateTraining);
    reset(initialCandidateTraining);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setIsEditMode(false);
    setEditingRecordId(null);
    setCurrentTraining(initialCandidateTraining);
    reset(initialCandidateTraining);
  };

  const prepareUpdate = (recordId) => {
    try {
      const training = data.find(
        (d) => (d.training_id || d.record_id || d.id) === recordId
      );
      if (!training) {
        toast.error('Training not found');
        return;
      }

      const preparedData = prepareTrainingForEdit(training);
      setIsEditMode(true);
      setEditingRecordId(recordId);
      setCurrentTraining(preparedData);
      reset(preparedData);
      setIsModalOpen(true);
    } catch (error) {
      console.error('Error in prepareUpdate:', error);
      toast.error(error.message || 'An error occurred while preparing the update');
    }
  };

  // Form submission
  const onSubmit = async (formData) => {
    if (!candidateId) {
      toast.error('Please select a candidate first');
      return;
    }

    setSubmitting(true);
    try {
      const payload = prepareTrainingPayload(formData, candidateId);

      if (isEditMode && editingRecordId) {
        // Update existing training
        await axiosInstance.put(
          `/training/${editingRecordId}?client=${clientId}&company=${companyId}`,
          payload
        );
        toast.success('Training Updated Successfully');
      } else {
        // Create new training
        await axiosInstance.post(
          `/training?client=${clientId}&company=${companyId}`,
          payload
        );
        toast.success('Training Added Successfully');
      }
      closeModal();
      fetchTrainings();
    } catch (error) {
      console.error('Error saving training:', error);
      const errorMessage = error.response?.data?.message || error.message || '';
      toast.error(errorMessage || 'Something Went Wrong');
    } finally {
      setSubmitting(false);
    }
  };

  // Fetch data
  const fetchTrainings = async () => {
    if (!candidateId) {
      setData([]);
      return;
    }
    setLoading(true);
    try {
      const res = await axiosInstance.get(
        `/training?client=${clientId}&company=${companyId}&candidateId=${candidateId}`
      );
      const records = res.data?.response?.data || res.data?.data?.records || [];
      setData(Array.isArray(records) ? records : []);
    } catch (err) {
      console.error('Error fetching trainings:', err);
      toast.error('Something Went Wrong');
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrainings();
  }, [candidateId]);

  // Search and filter handlers
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
  };

  const handleFilterClick = () => {
    setFilterPopupOpen(true);
  };

  const handleCloseFilterPopup = () => {
    setFilterPopupOpen(false);
  };

  const handleApplyFilters = (filters) => {
    setAppliedFilters(filters || []);
    setFilterPopupOpen(false);
  };

  // Filter search results for FilterPopup
  const getSearchResults = (searchTerm, selectedCategory) => {
    if (!searchTerm || !selectedCategory || !data) return [];

    return data
      .filter((item) => {
        let valueToSearch = '';
        switch (selectedCategory) {
          case 'Class Name':
            valueToSearch = item.class_name || '';
            break;
          case 'Status':
            valueToSearch = item.status || '';
            break;
          case 'Faculty Name':
            valueToSearch = item.faculty_name || '';
            break;
          default:
            valueToSearch = '';
        }
        return valueToSearch.toLowerCase().includes(searchTerm.toLowerCase());
      })
      .map((item) => {
        switch (selectedCategory) {
          case 'Class Name':
            return item.class_name;
          case 'Status':
            return item.status;
          case 'Faculty Name':
            return item.faculty_name;
          default:
            return null;
        }
      })
      .filter(Boolean)
      .filter((value, index, self) => self.indexOf(value) === index);
  };

  // Filter trainings based on search term and applied filters
  const filteredTrainings = data.filter((trainingItem) => {
    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch =
        (trainingItem.class_name || '').toLowerCase().includes(searchLower) ||
        (trainingItem.status || '').toLowerCase().includes(searchLower) ||
        (trainingItem.feedback || '').toLowerCase().includes(searchLower) ||
        (trainingItem.faculty_name || '').toLowerCase().includes(searchLower);
      if (!matchesSearch) return false;
    }

    // Apply category filters
    if (Array.isArray(appliedFilters) && appliedFilters.length > 0) {
      return appliedFilters.some(({ category, items }) => {
        if (!items || items.length === 0) return false;
        switch (category) {
          case 'Class Name':
            return items.some(
              (name) => name.toLowerCase() === (trainingItem.class_name || '').toLowerCase()
            );
          case 'Status':
            return items.some(
              (status) => status.toLowerCase() === (trainingItem.status || '').toLowerCase()
            );
          case 'Faculty Name':
            return items.some(
              (faculty) => faculty.toLowerCase() === (trainingItem.faculty_name || '').toLowerCase()
            );
          default:
            return false;
        }
      });
    }

    return true;
  });

  return (
    <div className="p-5">
      <div className="border border-gray-300 rounded-lg relative">
        {/* Header */}
        <CandidateTrainingsHeader
          onReload={fetchTrainings}
          onAddTraining={openModal}
          isLoading={isLoading}
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
        />

        {/* Table with loading overlay */}
        <div className="relative">
          <CandidateTrainingsTable
            data={filteredTrainings}
            onEditTraining={prepareUpdate}
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
            isFilterPopupOpen={isFilterPopupOpen}
            onFilterClick={handleFilterClick}
            onCloseFilterPopup={handleCloseFilterPopup}
            onApplyFilters={handleApplyFilters}
            getSearchResults={getSearchResults}
            filterCategories={filterCategories}
          />
          {isLoading && (
            <div
              className="absolute left-0 right-0 flex flex-col items-center justify-center z-20"
              style={{ top: '64px', bottom: 0, background: 'rgba(255,255,255,0.9)' }}
            >
              <div className="w-10 h-10 border-4 border-secondary-200 border-t-secondary-700 rounded-full animate-spin mb-3"></div>
              <span className="text-lg text-secondary-700 font-semibold flex items-center">
                Loading
                <span className="inline-flex ml-1">
                  <span className="animate-bounce" style={{ animationDelay: '0ms' }}>
                    .
                  </span>
                  <span className="animate-bounce" style={{ animationDelay: '150ms' }}>
                    .
                  </span>
                  <span className="animate-bounce" style={{ animationDelay: '300ms' }}>
                    .
                  </span>
                </span>
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Training Modal */}
      <CandidateTrainingsModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onSubmit={onSubmit}
        register={register}
        errors={errors}
        handleSubmit={handleSubmit}
        watch={watch}
        setValue={setValue}
        isSubmitting={isSubmitting}
        isEditMode={isEditMode}
        defaultValues={currentTraining}
        classes={classes}
        classesLoading={classesLoading}
      />
    </div>
  );
};

export default CandidateTrainings;
